import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, RefreshCw, Lock, Send, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  ["phone", "Phone"], ["location", "Based in"], ["hear_about", "Heard via"],
  ["hear_about_other", "Heard (other)"], ["referred_by", "Referred by"],
  ["drawing_now", "Why now"], ["change_one_thing", "Change one thing"],
  ["big_wish", "Big wish"], ["prior_work", "Prior work"], ["group_style", "In a group"],
  ["give_receive", "Give & receive"], ["room_preference", "Room preference"], ["health_considerations", "Health / dietary"],
  ["gentle_with", "Be gentle with"], ["can_move_deposit", "Can move on deposit"],
  ["anything_else", "Anything else"],
];

export default function AdminApplications() {
  const [token, setToken] = useState(localStorage.getItem("aw_admin_token") || "");
  const [input, setInput] = useState("");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const load = useCallback(async (t) => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/admin/applications`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.status === 401) { setError("That access code isn't right. Please try again."); setApps([]); localStorage.removeItem("aw_admin_token"); setToken(""); return; }
      const data = await res.json();
      setApps(data.applications || []);
    } catch (e) {
      setError("Couldn't load applications. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const unlock = (e) => {
    e.preventDefault();
    localStorage.setItem("aw_admin_token", input.trim());
    setToken(input.trim());
  };

  const [accepting, setAccepting] = useState(null);
  const accept = async (a) => {
    if (a.status === "accepted" && !window.confirm(`${a.name || a.email} was already sent an invite. Send the booking link again?`)) return;
    if (a.status !== "accepted" && !window.confirm(`Accept ${a.name || a.email}? This emails them a personal invite with your private booking link.`)) return;
    setAccepting(a.id);
    try {
      const res = await fetch(`${API}/admin/applications/${a.id}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ base_url: window.location.origin }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.invite_sent) {
        toast.success(`Invite sent to ${a.name || a.email} 🌿`);
      } else {
        toast.warning(`Marked accepted, but the invite email couldn't be delivered to ${a.email}. Please email them the booking link directly.`);
      }
      load(token);
    } catch (e) {
      toast.error("Couldn't send the invite. Please try again, or email them directly.");
    } finally {
      setAccepting(null);
    }
  };

  const setStatus = async (a, action) => {
    const labels = { waitlist: "waitlist", decline: "mark 'not this cohort' for" };
    if (!window.confirm(`Send a gentle ${action === "waitlist" ? "waitlist" : "'not this cohort'"} note to ${a.name || a.email}?`)) return;
    setAccepting(a.id);
    try {
      const res = await fetch(`${API}/admin/applications/${a.id}/${action}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast[data.notified ? "success" : "warning"](data.notified ? `Note sent to ${a.name || a.email} 🌿` : `Status saved, but the email couldn't be delivered to ${a.email}.`);
      load(token);
    } catch (e) {
      toast.error("Couldn't complete that. Please try again.");
    } finally {
      setAccepting(null);
    }
  };

  const [seats, setSeats] = useState(null);
  const loadSeats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/seats`);
      setSeats(await res.json());
    } catch (e) { /* non-critical */ }
  }, []);
  useEffect(() => { if (token) loadSeats(); }, [token, loadSeats]);
  const saveSeats = async (patch) => {
    try {
      const res = await fetch(`${API}/admin/seats`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error();
      setSeats(await res.json());
      toast.success("Seats updated");
    } catch (e) { toast.error("Couldn't update seats."); }
  };

  const [notes, setNotes] = useState({});
  const saveNote = async (a) => {
    try {
      const res = await fetch(`${API}/admin/applications/${a.id}/note`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note: notes[a.id] ?? a.note ?? "" }),
      });
      if (!res.ok) throw new Error();
      toast.success("Note saved");
      load(token);
    } catch (e) { toast.error("Couldn't save note."); }
  };

  const toggleDiscount = async (a) => {
    try {
      const res = await fetch(`${API}/admin/applications/${a.id}/discount`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: !a.discount_100 }),
      });
      if (!res.ok) throw new Error();
      toast.success(!a.discount_100 ? "$100 off granted — they'll pay $650 at checkout." : "$100 off removed — back to $750.");
      load(token);
    } catch (e) { toast.error("Couldn't update the discount."); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Permanently delete ${a.name || a.email}'s application? This can't be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/applications/${a.id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success(`Deleted ${a.name || a.email}`);
      setApps((prev) => prev.filter((x) => x.id !== a.id));
    } catch (e) { toast.error("Couldn't delete. Please try again."); }
  };

  const accepted = apps.filter((a) => a.status === "accepted").length;
  const waitlisted = apps.filter((a) => a.status === "waitlist").length;
  const declined = apps.filter((a) => a.status === "declined").length;
  const awaiting = apps.length - accepted - waitlisted - declined;

  if (!token) {
    return (
      <div className="min-h-screen bg-[#EAE5D9] grid place-items-center px-6">
        <form onSubmit={unlock} className="w-full max-w-sm text-center" data-testid="admin-login">
          <div className="w-14 h-14 rounded-full bg-[#2C2A28] grid place-items-center mx-auto"><Lock className="w-6 h-6 text-[#F7F5F0]" /></div>
          <h1 className="font-serif text-4xl tracking-tighter mt-6">Applications</h1>
          <p className="mt-2 text-[#595550] text-sm">Enter your private access code.</p>
          <input data-testid="admin-token-input" type="password" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Access code" className="mt-6 w-full bg-[#F7F5F0] border hairline rounded-sm px-4 py-3 outline-none focus:border-[#B85C38]" />
          {error && <p className="mt-3 text-sm text-[#B85C38]">{error}</p>}
          <button data-testid="admin-unlock-button" type="submit" className="reserve-cta mt-4 w-full bg-[#2C2A28] text-[#F7F5F0] py-3 rounded-full font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#B85C38]">Unlock</button>
          <Link to="/" className="block mt-6 font-mono-a text-[10px] uppercase tracking-[0.18em] text-[#595550] hover:text-[#B85C38]">← Back to site</Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EAE5D9]">
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <Link to="/" className="font-serif text-xl tracking-tight">The Ageless Woman</Link>
            <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter mt-2">Applications <span className="text-[#B85C38]">({apps.length})</span></h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={() => load(token)} className="inline-flex items-center gap-2 border hairline rounded-full px-4 py-2 font-mono-a text-[11px] uppercase tracking-[0.15em] hover:border-[#B85C38]">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={() => { localStorage.removeItem("aw_admin_token"); setToken(""); }} className="font-mono-a text-[11px] uppercase tracking-[0.15em] text-[#595550] hover:text-[#B85C38]">Lock</button>
          </div>
        </div>

        {seats && (
          <div data-testid="admin-seats" className="mt-6 bg-[#F7F5F0] border hairline rounded-sm px-6 py-4 flex items-center justify-between flex-wrap gap-4">
            <div className="font-mono-a text-[11px] uppercase tracking-[0.15em] text-[#595550]">
              Seats: <span className="text-[#B85C38]">{seats.remaining} of {seats.total} remaining</span> · {seats.taken} taken
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a]">
                Total
                <input data-testid="seats-total" type="number" min="1" defaultValue={seats.total}
                  onBlur={(e) => { const v = parseInt(e.target.value); if (v && v !== seats.total) saveSeats({ total: v }); }}
                  className="w-16 bg-white border hairline rounded-sm px-2 py-1 text-[#2C2A28]" />
              </label>
              <label className="flex items-center gap-2 font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a]">
                Booked offline
                <input data-testid="seats-offline" type="number" min="0" defaultValue={seats.offline ?? 0}
                  onBlur={(e) => { const v = parseInt(e.target.value); if (!isNaN(v)) saveSeats({ offline_taken: v }); }}
                  className="w-16 bg-white border hairline rounded-sm px-2 py-1 text-[#2C2A28]" />
              </label>
            </div>
          </div>
        )}

        {apps.length > 0 && (
          <div data-testid="admin-summary" className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ["Awaiting you", awaiting, "#B85C38"],
              ["Accepted", accepted, "#5C6B50"],
              ["Waitlisted", waitlisted, "#B8893C"],
              ["Not this cohort", declined, "#9a938a"],
            ].map(([label, n, color]) => (
              <div key={label} data-testid={`admin-summary-${label.split(" ")[0].toLowerCase()}`}
                className="bg-[#F7F5F0] border hairline rounded-sm px-4 py-4 text-center">
                <div className="font-serif text-4xl tracking-tighter" style={{ color }}>{n}</div>
                <div className="font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a] mt-1.5">{label}</div>
              </div>
            ))}
          </div>
        )}

        {loading && apps.length === 0 && <p className="mt-10 text-[#595550]">Loading…</p>}
        {!loading && apps.length === 0 && <p data-testid="admin-empty" className="mt-10 text-[#595550]">No applications yet. They'll appear here the moment someone applies.</p>}

        <div className="mt-8 space-y-5" data-testid="admin-list">
          {apps.map((a) => (
            <div key={a.id} data-testid="admin-app-card" className="bg-[#F7F5F0] border hairline rounded-sm p-6 lg:p-8">
              <div className="flex items-start justify-between flex-wrap gap-3 border-b hairline pb-4">
                <div>
                  <h2 className="font-serif text-2xl tracking-tight">{a.name || "—"}</h2>
                  <a href={`mailto:${a.email}`} className="text-[#B85C38] hover:underline">{a.email}</a>
                </div>
                <div className="text-right">
                  <div className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550]">
                    {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
                  </div>
                  <div className={`mt-2 inline-flex items-center gap-1.5 font-mono-a text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded-full ${a.fg_pushed ? "text-[#5C6B50] bg-[#5C6B50]/10" : "text-[#B85C38] bg-[#B85C38]/10"}`}>
                    {a.fg_pushed ? <><CheckCircle2 className="w-3 h-3" /> In CRM</> : <><AlertTriangle className="w-3 h-3" /> Not in CRM</>}
                  </div>
                  {a.can_travel && <div className="mt-1 font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#5C6B50]">✓ Can travel Apr</div>}
                  {a.discount_100 && (
                    <div data-testid="admin-discount-badge" className="mt-1 font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#B85C38]">
                      ★ $100 off · pays $650 at checkout
                    </div>
                  )}
                  <button data-testid="admin-discount-toggle" onClick={() => toggleDiscount(a)}
                    title="Toggle the $100 Curiosity Chat discount"
                    className="mt-1 block ml-auto font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a] hover:text-[#B85C38]">
                    {a.discount_100 ? "Remove $100 off" : "Grant $100 off"}
                  </button>
                  <button data-testid="admin-delete-button" onClick={() => remove(a)}
                    title="Delete this application"
                    className="mt-2 inline-flex items-center gap-1.5 font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a] hover:text-[#B85C38]">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
              <dl className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {FIELDS.map(([k, lbl]) => a[k] ? (
                  <div key={k} className="text-sm">
                    <dt className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#9a938a]">{lbl}</dt>
                    <dd className="text-[#4a4642] mt-0.5 whitespace-pre-wrap">{a[k]}</dd>
                  </div>
                ) : null)}
              </dl>
              <div className="mt-4 pt-4 border-t hairline">
                <label className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#9a938a]">Private note (only you & Natalie see this)</label>
                <textarea data-testid="admin-note" rows={2}
                  defaultValue={a.note || ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [a.id]: e.target.value }))}
                  placeholder="Jot a thought about this applicant…"
                  className="mt-2 w-full bg-white border hairline rounded-sm px-3 py-2 text-sm text-[#4a4642] outline-none focus:border-[#B85C38] resize-none" />
                <button data-testid="admin-note-save" onClick={() => saveNote(a)}
                  className="mt-2 font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] border-b border-[#B85C38] pb-0.5 hover:text-[#B85C38]">
                  Save note
                </button>
              </div>
              <div className="mt-4 pt-4 border-t hairline flex items-center justify-between flex-wrap gap-3">
                {a.status === "accepted" ? (
                  <span data-testid="admin-status-badge" className="inline-flex items-center gap-1.5 font-mono-a text-[11px] uppercase tracking-[0.12em] text-[#5C6B50]">
                    <CheckCircle2 className="w-4 h-4" /> Accepted{a.invite_sent ? " · invite sent" : ""}
                  </span>
                ) : a.status === "waitlist" ? (
                  <span data-testid="admin-status-badge" className="font-mono-a text-[11px] uppercase tracking-[0.12em] text-[#B8893C]">◐ Waitlisted{a.notified ? " · notified" : ""}</span>
                ) : a.status === "declined" ? (
                  <span data-testid="admin-status-badge" className="font-mono-a text-[11px] uppercase tracking-[0.12em] text-[#9a938a]">Not this cohort{a.notified ? " · notified" : ""}</span>
                ) : (
                  <span className="font-mono-a text-[11px] uppercase tracking-[0.12em] text-[#9a938a]">Awaiting your decision</span>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <button data-testid="admin-waitlist-button" onClick={() => setStatus(a, "waitlist")} disabled={accepting === a.id}
                    className="border hairline rounded-full px-4 py-2.5 font-mono-a text-[11px] uppercase tracking-[0.12em] text-[#B8893C] hover:border-[#B8893C] disabled:opacity-50">
                    Waitlist
                  </button>
                  <button data-testid="admin-decline-button" onClick={() => setStatus(a, "decline")} disabled={accepting === a.id}
                    className="border hairline rounded-full px-4 py-2.5 font-mono-a text-[11px] uppercase tracking-[0.12em] text-[#9a938a] hover:border-[#595550] disabled:opacity-50">
                    Not this cohort
                  </button>
                  <button data-testid="admin-accept-button" onClick={() => accept(a)} disabled={accepting === a.id}
                    className="reserve-cta inline-flex items-center gap-2 bg-[#2C2A28] text-[#F7F5F0] px-5 py-2.5 rounded-full font-mono-a text-[11px] uppercase tracking-[0.15em] hover:bg-[#B85C38] disabled:opacity-50">
                    <Send className="w-3.5 h-3.5" />
                    {accepting === a.id ? "Sending…" : a.status === "accepted" ? "Resend link" : "Accept & send link"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
